import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';

/**
 * HeatmapLayer — overlay for Leaflet map showing density of complaints.
 *
 * Props:
 *   - events: array of {location: {lat, lng}, category, priority}
 *   - visible: bool — show/hide the heatmap layer
 */
const HeatmapLayer = ({ events, visible }) => {
  const map = useMap();
  const heatLayerRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // Remove existing layer
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
      heatLayerRef.current = null;
    }

    if (!visible || !events || events.length === 0) return;

    // Convert events to [lat, lng, intensity] tuples
    // Emergency events have weight 1.0, normal events 0.6
    const points = events
      .filter(e => e.location && typeof e.location.lat === 'number')
      .map(e => {
        const intensity = e.category === 'emergency' ? 1.0
          : (e.priority >= 3 ? 0.85 : 0.55);
        return [e.location.lat, e.location.lng, intensity];
      });

    if (points.length === 0) return;

    const layer = L.heatLayer(points, {
      radius: 30,
      blur: 22,
      maxZoom: 17,
      minOpacity: 0.35,
      gradient: {
        0.2: '#3388ff',
        0.4: '#22e000',
        0.6: '#ffeb3b',
        0.8: '#ff9800',
        1.0: '#e53935'
      }
    });

    layer.addTo(map);
    heatLayerRef.current = layer;

    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }
    };
  }, [map, events, visible]);

  return null;
};

export default HeatmapLayer;
